import { assignTableAPIs, type TableFeature } from "@tanstack/react-table";

export interface FooterRowCountFeatureConstructors {
  Table: {
    getFooterRowCount: () => number;
    getFooterSelectedRowCount: () => number;
    getFooterTotalRowCount: () => number;
    getFooterRowCountLabel: () => string;
  };
  TableOptions: {
    enableFooterRowCount?: boolean;
    footerRowCountLabel?: (visibleRows: number, totalRows: number, selectedRows: number) => string;
  };
}

function pluraliseRows(count: number) {
  return `${count.toLocaleString()} row${count === 1 ? "" : "s"}`;
}

function isGroupedRow(row: { getIsGrouped?: () => boolean }) {
  return row.getIsGrouped?.() ?? false;
}

function getVisibleRowCount(
  table: Parameters<NonNullable<TableFeature<FooterRowCountFeatureConstructors>["constructTableAPIs"]>>[0],
) {
  return table.getFilteredRowModel().flatRows.filter((row) => !isGroupedRow(row)).length;
}

function getTotalRowCount(
  table: Parameters<NonNullable<TableFeature<FooterRowCountFeatureConstructors>["constructTableAPIs"]>>[0],
) {
  return table.getCoreRowModel().flatRows.length;
}

function getSelectedRowCount(
  table: Parameters<NonNullable<TableFeature<FooterRowCountFeatureConstructors>["constructTableAPIs"]>>[0],
) {
  const selectedRowModel = (table as typeof table & {
    getFilteredSelectedRowModel: () => ReturnType<typeof table.getFilteredRowModel>;
  }).getFilteredSelectedRowModel();

  return selectedRowModel.flatRows.filter((row) => !isGroupedRow(row)).length;
}

function hasSelectionColumn(
  table: Parameters<NonNullable<TableFeature<FooterRowCountFeatureConstructors>["constructTableAPIs"]>>[0],
) {
  return table.getAllLeafColumns().some((column) => column.columnDef.meta?.selection === true);
}

export function constructFooterRowCountFeature(): TableFeature<FooterRowCountFeatureConstructors> {
  return {
    getDefaultTableOptions: () => ({
      enableFooterRowCount: true,
    }),
    constructTableAPIs: (table) => {
      assignTableAPIs("footerRowCountFeature" as never, table, {
        table_getFooterRowCount: {
          fn: () => getVisibleRowCount(table),
          memoDeps: () => [table.getFilteredRowModel()],
        },
        table_getFooterSelectedRowCount: {
          fn: () => getSelectedRowCount(table),
          memoDeps: () => [getSelectedRowCount(table)],
        },
        table_getFooterTotalRowCount: {
          fn: () => getTotalRowCount(table),
          memoDeps: () => [table.getCoreRowModel()],
        },
        table_getFooterRowCountLabel: {
          fn: () => {
            const visibleRows = getVisibleRowCount(table);
            const totalRows = getTotalRowCount(table);
            const selectedRows = getSelectedRowCount(table);

            if (table.options.footerRowCountLabel) {
              return table.options.footerRowCountLabel(visibleRows, totalRows, selectedRows);
            }

            if (!hasSelectionColumn(table)) {
              return pluraliseRows(visibleRows);
            }

            return `${selectedRows.toLocaleString()} of ${pluraliseRows(visibleRows)} selected`;
          },
          memoDeps: () => [
            getVisibleRowCount(table),
            getTotalRowCount(table),
            getSelectedRowCount(table),
            hasSelectionColumn(table),
            table.options.footerRowCountLabel,
          ],
        },
      });
    },
  };
}

export const footerRowCountFeature = constructFooterRowCountFeature();
